import { useCallback, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { useRouter } from 'next/router';

import {
  initProductDetail,
  initProductDetailCouponBanner,
  setDefaultContentId,
} from '../slice';
import { useAppSelector } from '../../../shared/store';
import {
  loadAdultVerificationData,
  saveAdultVerificationData,
} from '../../../member/adult-verification/services/adult-verificaiton-data-storage';
import Alert from '../../../shared/components/Alert/Alert';
import { isPC } from '../../../../util/window/getDevice';
import { notify, redirectTo } from '../../../shared/reducers/page';
import {
  loadCartItems,
  storeCartItems,
} from '../../../cart/shared/services/cart-items.storage.service';
import { setCartItems } from '../../../cart/shared/reducers/cart.slice';
import {
  loadBaseAddressNotification,
  loadCartAddress,
} from '../../../shared/reducers/shipping-address.slice';
import {
  ProductDetailServerSideProps,
} from '../../../shared/utils/server-side-props';
import useRecentProducts from './useRecentProducts';
import {
  loadSessionStorage,
} from '../../../shared/services/session.storage.service';
import { branchService } from '../../../shared/branch';
import { ViewItem } from '../../../shared/branch/events';
import { amplitudeService, ScreenName } from '../../../shared/amplitude';
import {
  ViewProductDetail,
} from '../../../shared/amplitude/events/product/ViewProductDetail';
import { getFusionQueryId } from '../shared/utils/productDetailEvent';
import { setQueryId } from '../../list/slice';

export default function useProductDetailPage({
  product,
  adultVerificationFailed,
  verificationEmployeeFailed,
  error,
}: ProductDetailServerSideProps) {
  const dispatch = useDispatch();
  const router = useRouter();

  const { hasSession, isGuest } = useAppSelector(({ auth }) => auth);
  const { cartItems } = useAppSelector(({ cart }) => cart);
  const { queryId } = useAppSelector(({ productList }) => productList);

  const fusionQueryId = router.query.queryId as string ?? queryId;

  useEffect(() => {
    if (router.query.queryId) {
      dispatch(setQueryId(router.query.queryId));
    }
  }, [dispatch, router.query.queryId]);

  const verifyAdultForAdultProduct = useCallback(async () => {
    if (!hasSession || !product || !product.isOnlyAdult || !adultVerificationFailed) {
      return;
    }

    const data = loadAdultVerificationData();

    const referrerUrl: string =
      data?.referrer_url || loadSessionStorage('prevPath') || encodeURIComponent(window.location.href);

    saveAdultVerificationData({
      referrer_url: referrerUrl,
      goodsno: product.no,
    });

    if (isGuest) {
      const { isConfirmed } = await Alert({
        text: '로그인하셔야 본 서비스를 이용하실 수 있습니다.',
        allowOutsideClick: false,
      });

      if (isConfirmed) {
        const url = isPC ? '/shop/member/login.php' : '/m2/mem/login.php';
        const queries = `?return_url=${encodeURIComponent(window.location.href)}&authAdult=true`;
        window.location.href = url + queries;
      }

      return;
    }

    await router.push('/member/adult-verification');
  }, [hasSession, isGuest, product, adultVerificationFailed, router]);

  useEffect(() => {
    verifyAdultForAdultProduct()
      .catch(err => dispatch(notify(err.message)));
  }, [verifyAdultForAdultProduct, dispatch]);

  useEffect(() => {
    if (!product) {
      Alert({ text: error ? error.message : '일시적인 오류로 상품 정보를 불러 올 수 없습니다.' })
        .then(() => {
          dispatch(redirectTo({ url: '/', isExternal: true }));
        });
      return;
    }

    dispatch(setDefaultContentId(product.no));
    dispatch(initProductDetail(product));
  }, [dispatch, product]);

  useEffect(() => {
    if (!adultVerificationFailed && product && hasSession) {
      dispatch(initProductDetailCouponBanner(product.no));
    }
  }, [adultVerificationFailed, hasSession, product, dispatch]);

  useEffect(() => {
    if (isGuest) {
      const localCartItems = loadCartItems();

      if (localCartItems) {
        dispatch(setCartItems(localCartItems));
      }

      return;
    }

    storeCartItems([]);
  }, [dispatch, isGuest]);

  useEffect(() => {
    if (isGuest) {
      storeCartItems(cartItems);
    }
  }, [cartItems, isGuest]);

  useEffect(() => {
    if (!hasSession) {
      return;
    }
    if (isGuest) {
      dispatch(loadCartAddress());
      return;
    }
    dispatch(loadBaseAddressNotification());
  }, [dispatch, hasSession, isGuest]);

  useRecentProducts(product);

  useEffect(() => {
    sessionStorage.setItem('goodsListReferrer', 'goodsView');
  }, []);

  useEffect(() => {
    if (product) {
      try {
        amplitudeService.setScreenName(ScreenName.PRODUCT_DETAIL_DESCRIPTION)
          .then(() => {
            amplitudeService
              .logEvent(new ViewProductDetail({ product, fusionQueryId: getFusionQueryId(fusionQueryId) }))
              .catch(); // ignore
          });

        const { no, name, basePrice, discountedPrice } = product;
        branchService.logEvent(
          new ViewItem([{
            $canonical_identifier: `product/${no}`,
            $sku: no,
            $price: discountedPrice || basePrice,
            $quantity: 1,
            $currency: 'KRW',
            $product_name: name,
          }]),
        );
      } catch (e) {}
    }

    if (error && verificationEmployeeFailed) {
      Alert({ text: error.message })
        .then(() => dispatch(redirectTo({ url: '/main' })));
    }

    if (error) {
      console.error(error.log);
    }
  }, [dispatch, error, fusionQueryId, product, router, verificationEmployeeFailed]);
}
